/* eslint-disable max-lines */
import { observable, toJS } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { diff } from 'deep-object-diff';
import type { OrderItemPriceResponseArgs } from './order_item_price/order_item_price2';
import OrderItemPrice, { convertOrderItemPriceResponseToArgs } from './order_item_price/order_item_price2';
import type { OrderItemMappingResponseArgs } from './order_item_mapping2';
import OrderItemMapping, { convertOrderItemMappingResponseToArgs } from './order_item_mapping2';
import type { TravelerInformationResponseArgs } from './traveler_information';
import TravelerInformation, { convertTravelerInformationResponseToArgs } from './traveler_information';
import type { HotelElementResponseArgs } from './hotel_element2';
import type { TransportElementResponseArgs, TransportType } from './transport_element2';
import HotelElement, { convertHotelElementResponseToArgs } from './hotel_element2';
import TransportElement, { convertTransportElementResponseToArgs } from './transport_element2';
import TransportElementTerminal from './transport_element_terminal/transport_element_terminal2';
import type { CancelChargeElementResponseArgs } from './cancel_charge_element2';
import CancelChargeElement, { convertCancelChargeElementResponseToArgs } from './cancel_charge_element2';
import type { ChangeFeeElementResponseArgs } from './change_fee_element2';
import ChangeFeeElement, { convertChangeFeeElementResponseToArgs } from './change_fee_element2';
import type { SupportFeeElementResponseArgs } from './support_fee_element/support_fee_element2';
import SupportFeeElement, {
  convertSupportFeeElementResponseToArgs
} from './support_fee_element/support_fee_element2';
import type { ShippingElementResponseArgs } from './shipping_element/shipping_element2';
import ShippingElement, { convertShippingElementResponseToArgs } from './shipping_element/shipping_element2';
import type { WifiElementResponseArgs } from './wifi_element/wifi_element2';
import WifiElement, { convertWifiElementResponseToArgs } from './wifi_element/wifi_element2';
import type { RoomElementResponseArgs } from './room_element/room_element2';
import RoomElement, { convertRoomElementResponseToArgs } from './room_element/room_element2';
import type { KitElementResponseArgs } from './kit_element/kit_element2';
import KitElement, { convertKitElementResponseToArgs } from './kit_element/kit_element2';
import type { TravelAuthorizationElementResponseArgs } from './travel_authorization_element2';
import TravelAuthorizationElement, {
  convertTravelAuthorizationElementResponseToArgs
} from './travel_authorization_element2';
import type { InsuranceElementResponseArgs } from './insurance_element2';
import InsuranceElement, { convertInsuranceElementResponseToArgs } from './insurance_element2';
import type { PrefectureResponseArgs } from './prefecture2';
import Prefecture, { convertPrefectureResponseToArgs } from './prefecture2';
import type { MunicipalityResponseArgs } from './municipality';
import Municipality, { convertMunicipalityResponseToArgs } from './municipality';
import type { MarginTypeArgs } from './organization/margin_type2';
import MarginType from './organization/margin_type2';
import type { OrderItemPaymentResponseArgs } from './order_item_payment/order_item_payment2';
import OrderItemPayment, { convertOrderItemPaymentResponseToArgs } from './order_item_payment/order_item_payment2';
import type { OrderItemJobTypeKey } from './order_item/order_item_job_type';
import { Fetcher } from '../util/fetcher/fetcher.util';
import TravelerShinkansenType from './traveler/traveler_shinkansen_type';
import type { FlightJson } from './select_repository';
import Flight from './flight/flight';
import type { ETicketResponseArgs } from './e_ticket/e_ticket2';
import ETicket, { convertETicketResponseToArgs } from './e_ticket/e_ticket2';
import type { OrderItemCalendarEventResponseArgs } from './order_item_calendar_event';
import OrderItemCalendarEvent, { convertOrderItemCalendarEventResponseToArgs } from './order_item_calendar_event';
import type { OrderResponseArgs } from './order2';
import Order, { convertOrderResponseToArgs } from './order2';

export type Element =
  | TransportElement
  | HotelElement
  | CancelChargeElement
  | ChangeFeeElement
  | SupportFeeElement
  | ShippingElement
  | WifiElement
  | RoomElement
  | KitElement
  | TravelAuthorizationElement
  | InsuranceElement;

export type OrderItemResponseArgs = {
  id: number;
  trace_id?: number;
  order_item_category: string;
  order_item_category_text: string;
  price?: OrderItemPriceResponseArgs;
  payment?: OrderItemPaymentResponseArgs;
  order_item_mappings: OrderItemMappingResponseArgs[];
  traveler_informations: TravelerInformationResponseArgs[];
  transports?: TransportElementResponseArgs[];
  hotels?: HotelElementResponseArgs[];
  cancel_charges?: CancelChargeElementResponseArgs[];
  change_fees?: ChangeFeeElementResponseArgs[];
  support_fees?: SupportFeeElementResponseArgs[];
  shippings?: ShippingElementResponseArgs[];
  wifis?: WifiElementResponseArgs[];
  rooms?: RoomElementResponseArgs[];
  kits?: KitElementResponseArgs[];
  travel_authorizations?: TravelAuthorizationElementResponseArgs[];
  insurances?: InsuranceElementResponseArgs[];
  order_item_calender_events?: OrderItemCalendarEventResponseArgs[];
  sold_out?: boolean;
  first_item?: OrderItemResponseArgs;
  first_price?: OrderItemPriceResponseArgs;
  jr_auth_code?: string;
  jr_pickup_code?: string;
  order_item_type?: number;
  updated_at?: string;
  deleted_at?: string | null;
  status?: number;
  package_booking_url?: string;
  job_type?: OrderItemJobTypeKey;
  billed_at?: string | null;
  pnr_id?: string;
  foreign_flight_ticket_issuer?: string;
  purchase_deadline?: string | null;
  additional_type?: string;
  hotel_price_prefecture?: PrefectureResponseArgs;
  hotel_price_municipality?: MunicipalityResponseArgs;
  hotel_price_limit?: number;
  margin_type?: MarginTypeArgs;
  people_num?: number;
  ticketed_at?: string;
  delivered_at?: string;
  approved_at?: string;
  tsd_invoice_number?: string;
  waiting?: boolean;
  flight?: FlightJson;
  ticketing_method?: string;
  e_tickets?: ETicketResponseArgs[];
  order_item_calendar_events?: OrderItemCalendarEventResponseArgs[];
  buffer_amount?: number;
  order?: OrderResponseArgs;
};

export type OrderItemArgs = {
  id: number;
  traceId?: number;
  showFee: boolean;
  orderItemCategory: string;
  orderItemCategoryText: string;
  price?: OrderItemPrice;
  payment?: OrderItemPayment;
  orderItemMappings: OrderItemMapping[];
  travelerInformations: TravelerInformation[];
  transports: TransportElement[];
  hotels: HotelElement[];
  cancelCharges: CancelChargeElement[];
  changeFees: ChangeFeeElement[];
  supportFees: SupportFeeElement[];
  shippings: ShippingElement[];
  wifis: WifiElement[];
  wifiProviderOptions?: { [key: string]: string };
  shippingCategoryOptions?: { [key: string]: string };
  hotelElementProviderOptions?: { [key: string]: string };
  rooms: RoomElement[];
  kits: KitElement[];
  travelAuthorizations: TravelAuthorizationElement[];
  insurances: InsuranceElement[];
  soldOut: boolean;
  firstItem?: OrderItem;
  firstPrice?: OrderItemPrice;
  jrAuthCode?: string;
  jrPickupCode?: string;
  orderItemType?: number;
  updatedAt?: Moment;
  deletedAt?: Moment;
  isEditing?: boolean;
  status?: number;
  packageBookingUrl?: string;
  jobType?: OrderItemJobTypeKey;
  billedAt?: Moment | null;
  pnrId?: string;
  foreignFlightTicketIssuer?: string;
  purchaseDeadline?: Moment | null;
  additionalType?: string;
  hotelPricePrefecture?: Prefecture;
  hotelPriceMunicipality?: Municipality;
  hotelPriceLimit?: number;
  marginType?: MarginType;
  peopleNum?: number;
  ticketedAt?: Moment;
  deliveredAt?: Moment;
  approvedAt?: Moment;
  tsdInvoiceNumber?: string;
  waiting?: boolean;
  flight?: Flight;
  ticketingMethod?: string;
  eTickets?: ETicket[];
  orderItemCalendarEvents?: OrderItemCalendarEvent[];
  bufferAmount?: number;
  order?: Order;
};

export const convertOrderItemResponseToArgs = (
  response: OrderItemResponseArgs,
  showFee: boolean,
  wifiProviderOptions?: { [key: string]: string },
  shippingCategoryOptions?: { [key: string]: string },
  hotelElementProviderOptions?: { [key: string]: string }
) => {
  const args: OrderItemArgs = {
    id: response.id,
    traceId: response.trace_id,
    showFee,
    orderItemCategory: response.order_item_category,
    orderItemCategoryText: response.order_item_category_text,
    price: response.price
      ? new OrderItemPrice(
          convertOrderItemPriceResponseToArgs(
            response.price,
            showFee,
            response.margin_type ? new MarginType(response.margin_type) : undefined
          )
        )
      : undefined,
    payment: response.payment
      ? new OrderItemPayment(convertOrderItemPaymentResponseToArgs(response.payment))
      : undefined,
    orderItemMappings:
      response.order_item_mappings?.map(r => new OrderItemMapping(convertOrderItemMappingResponseToArgs(r))) || [],
    travelerInformations:
      response.traveler_informations?.map(
        r => new TravelerInformation(convertTravelerInformationResponseToArgs(r))
      ) || [],
    transports:
      response.transports?.map(r => new TransportElement(convertTransportElementResponseToArgs(r))) || [],
    hotels:
      response.hotels?.map(
        r => new HotelElement(convertHotelElementResponseToArgs(r, hotelElementProviderOptions))
      ) || [],
    cancelCharges:
      response.cancel_charges?.map(r => new CancelChargeElement(convertCancelChargeElementResponseToArgs(r))) ||
      [],
    changeFees:
      response.change_fees?.map(r => new ChangeFeeElement(convertChangeFeeElementResponseToArgs(r))) || [],
    supportFees:
      response.support_fees?.map(r => new SupportFeeElement(convertSupportFeeElementResponseToArgs(r))) || [],
    shippings:
      response.shippings?.map(
        r => new ShippingElement(convertShippingElementResponseToArgs(r, shippingCategoryOptions))
      ) || [],
    wifis:
      response.wifis?.map(r => new WifiElement(convertWifiElementResponseToArgs(r, wifiProviderOptions))) || [],
    wifiProviderOptions,
    shippingCategoryOptions,
    hotelElementProviderOptions,
    rooms: response.rooms?.map(r => new RoomElement(convertRoomElementResponseToArgs(r))) || [],
    kits: response.kits?.map(r => new KitElement(convertKitElementResponseToArgs(r))) || [],
    travelAuthorizations:
      response.travel_authorizations?.map(
        r => new TravelAuthorizationElement(convertTravelAuthorizationElementResponseToArgs(r))
      ) || [],
    insurances:
      response.insurances?.map(r => new InsuranceElement(convertInsuranceElementResponseToArgs(r))) || [],
    soldOut: response.sold_out || false,
    firstItem: response.first_item
      ? new OrderItem(
          convertOrderItemResponseToArgs(
            response.first_item,
            showFee,
            wifiProviderOptions,
            shippingCategoryOptions,
            hotelElementProviderOptions
          )
        )
      : undefined,
    firstPrice: response.first_price
      ? new OrderItemPrice(
          convertOrderItemPriceResponseToArgs(
            response.first_price,
            showFee,
            response.margin_type ? new MarginType(response.margin_type) : undefined
          )
        )
      : undefined,
    jrAuthCode: response.jr_auth_code,
    jrPickupCode: response.jr_pickup_code,
    orderItemType: response.order_item_type,
    updatedAt: response.updated_at ? moment(response.updated_at) : undefined,
    deletedAt: response.deleted_at ? moment(response.deleted_at) : undefined,
    isEditing: false,
    status: response.status,
    packageBookingUrl: response.package_booking_url,
    jobType: response.job_type,
    billedAt: response.billed_at ? moment(response.billed_at) : null,
    pnrId: response.pnr_id,
    foreignFlightTicketIssuer: response.foreign_flight_ticket_issuer,
    purchaseDeadline: response.purchase_deadline ? moment(response.purchase_deadline) : null,
    additionalType: response.additional_type,
    hotelPricePrefecture: response.hotel_price_prefecture
      ? new Prefecture(convertPrefectureResponseToArgs(response.hotel_price_prefecture))
      : undefined,
    hotelPriceMunicipality: response.hotel_price_municipality
      ? new Municipality(convertMunicipalityResponseToArgs(response.hotel_price_municipality))
      : undefined,
    hotelPriceLimit: response.hotel_price_limit,
    marginType: response.margin_type ? new MarginType(response.margin_type) : undefined,
    peopleNum: response.people_num,
    ticketedAt: response.ticketed_at ? moment(response.ticketed_at) : undefined,
    deliveredAt: response.delivered_at ? moment(response.delivered_at) : undefined,
    approvedAt: response.approved_at ? moment(response.approved_at) : undefined,
    tsdInvoiceNumber: response.tsd_invoice_number,
    waiting: response.waiting || false,
    flight: response.flight ? new Flight(response.flight) : undefined,
    ticketingMethod: response.ticketing_method,
    eTickets: response.e_tickets?.map(r => new ETicket(convertETicketResponseToArgs(r))) || [],
    orderItemCalendarEvents:
      response.order_item_calendar_events?.map(
        r => new OrderItemCalendarEvent(convertOrderItemCalendarEventResponseToArgs(r))
      ) || [],
    bufferAmount: response.buffer_amount,
    order: response.order ? new Order(convertOrderResponseToArgs(response.order, showFee)) : undefined
  };
  return args;
};

export default class OrderItem {
  @observable id: number;
  @observable traceId?: number;
  @observable showFee: boolean;
  @observable orderItemCategory: string;
  @observable orderItemCategoryText: string;
  @observable price: OrderItemPrice;
  @observable payment: OrderItemPayment;
  @observable orderItemMappings: OrderItemMapping[];
  @observable travelerInformations: TravelerInformation[];
  @observable transports: TransportElement[];
  @observable hotels: HotelElement[];
  @observable cancelCharges: CancelChargeElement[];
  @observable changeFees: ChangeFeeElement[];
  @observable supportFees: SupportFeeElement[];
  @observable shippings: ShippingElement[];
  @observable wifis: WifiElement[];
  @observable wifiProviderOptions?: { [key: string]: string };
  @observable shippingCategoryOptions?: { [key: string]: string };
  @observable hotelElementProviderOptions?: { [key: string]: string };
  @observable rooms: RoomElement[];
  @observable kits: KitElement[];
  @observable travelAuthorizations: TravelAuthorizationElement[];
  @observable insurances: InsuranceElement[];
  @observable elements: Element[];
  @observable soldOut: boolean;
  @observable firstItem?: OrderItem;
  @observable firstPrice?: OrderItemPrice;
  @observable jrAuthCode?: string;
  @observable jrPickupCode?: string;
  @observable orderItemType?: number;
  @observable updatedAt?: Moment;
  @observable deletedAt?: Moment;
  @observable isEditing: boolean;
  @observable status?: number;
  @observable packageBookingUrl?: string;
  @observable jobType?: OrderItemJobTypeKey;
  @observable billedAt?: Moment | null;
  @observable pnrId?: string;
  @observable foreignFlightTicketIssuer?: string;
  @observable purchaseDeadline?: Moment | null;
  @observable additionalType: string;
  @observable hotelPricePrefecture?: Prefecture;
  @observable hotelPriceMunicipality?: Municipality;
  @observable hotelPriceLimit?: number;
  @observable marginType?: MarginType;
  @observable peopleNum?: number;
  @observable ticketedAt?: Moment;
  @observable deliveredAt?: Moment;
  @observable approvedAt?: Moment;
  @observable initialData?: OrderItem;
  @observable tsdInvoiceNumber?: string;
  @observable waiting?: boolean;
  @observable flight?: Flight;
  @observable ticketingMethod?: string;
  @observable eTickets: ETicket[];
  @observable orderItemCalendarEvents: OrderItemCalendarEvent[];
  @observable bufferAmount?: number;
  @observable order?: Order;
  private travelerShinkansenSeatTypesCache?: { [key: string]: string[] };
  private travelerShinkansenTicketTypesCache?: { [key: string]: string[] };

  constructor(args: OrderItemArgs) {
    this.id = args.id;
    this.traceId = args.traceId;
    this.showFee = args.showFee;
    this.orderItemCategory = args.orderItemCategory;
    this.orderItemCategoryText = args.orderItemCategoryText;
    this.price = args.price || new OrderItemPrice({});
    this.payment = args.payment || new OrderItemPayment({});
    this.orderItemMappings = args.orderItemMappings;
    this.travelerInformations = args.travelerInformations;
    this.transports = args.transports;
    this.hotels = args.hotels;
    this.cancelCharges = args.cancelCharges;
    this.changeFees = args.changeFees;
    this.supportFees = args.supportFees;
    this.shippings = args.shippings;
    this.wifis = args.wifis;
    this.wifiProviderOptions = args.wifiProviderOptions;
    this.shippingCategoryOptions = args.shippingCategoryOptions;
    this.hotelElementProviderOptions = args.hotelElementProviderOptions;
    this.rooms = args.rooms;
    this.kits = args.kits;
    this.travelAuthorizations = args.travelAuthorizations;
    this.insurances = args.insurances;
    this.elements = [
      ...this.transports,
      ...this.hotels,
      ...this.cancelCharges,
      ...this.changeFees,
      ...this.supportFees,
      ...this.shippings,
      ...this.wifis,
      ...this.rooms,
      ...this.kits,
      ...this.travelAuthorizations,
      ...this.insurances
    ];
    this.soldOut = args.soldOut;
    this.firstItem = args.firstItem;
    this.firstPrice = args.firstPrice;
    this.jrAuthCode = args.jrAuthCode;
    this.jrPickupCode = args.jrPickupCode;
    this.orderItemType = args.orderItemType;
    this.updatedAt = args.updatedAt;
    this.deletedAt = args.deletedAt;
    this.isEditing = args.isEditing || false;
    this.status = args.status;
    this.packageBookingUrl = args.packageBookingUrl;
    this.jobType = args.jobType;
    this.billedAt = args.billedAt;
    this.pnrId = args.pnrId;
    this.foreignFlightTicketIssuer = args.foreignFlightTicketIssuer;
    this.purchaseDeadline = args.purchaseDeadline;
    this.additionalType = args.additionalType || 'hotel';
    this.hotelPricePrefecture = args.hotelPricePrefecture;
    this.hotelPriceMunicipality = args.hotelPriceMunicipality;
    this.hotelPriceLimit = args.hotelPriceLimit;
    this.marginType = args.marginType;
    this.peopleNum = args.peopleNum;
    this.ticketedAt = args.ticketedAt;
    this.deliveredAt = args.deliveredAt;
    this.approvedAt = args.approvedAt;
    this.tsdInvoiceNumber = args.tsdInvoiceNumber;
    this.waiting = args.waiting;
    this.flight = args.flight;
    this.ticketingMethod = args.ticketingMethod;
    this.eTickets = args.eTickets || [];
    this.orderItemCalendarEvents = args.orderItemCalendarEvents || [];
    this.bufferAmount = args.bufferAmount;
    // MobXのobservableをプレーンなオブジェクトに変換してからクローン
    this.initialData = cloneDeep(toJS(this));
    this.order = args.order;
  }

  get isPriceChanged() {
    return !!(
      this.firstItem &&
      this.price &&
      this.firstItem.price &&
      this.price.totalPrice !== this.firstItem.price.totalPrice
    );
  }

  travelerShinkansenSeatTypes(elementIndex: number | null = null) {
    if (this.orderItemCategory !== 'shinkansen' && this.orderItemCategory !== 'express') {
      return [];
    }

    this.travelerShinkansenSeatTypesCache = this.travelerShinkansenSeatTypesCache || {};
    const cacheKey = elementIndex?.toString() || 'null';
    if (this.travelerShinkansenSeatTypesCache[cacheKey]) {
      return this.travelerShinkansenSeatTypesCache[cacheKey];
    }

    this.travelerShinkansenSeatTypesCache[cacheKey] = this.travelerInformations.map(travelerInformation =>
      travelerInformation.getShinkansenSeatTypeText(elementIndex)
    );
    return this.travelerShinkansenSeatTypesCache[cacheKey];
  }

  travelerShinkansenTicketTypes(elementIndex: number | null = null) {
    if (this.orderItemCategory !== 'shinkansen' && this.orderItemCategory !== 'express') {
      return [];
    }

    this.travelerShinkansenTicketTypesCache = this.travelerShinkansenTicketTypesCache || {};
    const cacheKey = elementIndex?.toString() || 'null';
    if (this.travelerShinkansenTicketTypesCache[cacheKey]) {
      return this.travelerShinkansenTicketTypesCache[cacheKey];
    }

    this.travelerShinkansenTicketTypesCache[cacheKey] = this.travelerInformations.map(travelerInformation =>
      travelerInformation.getShinkansenTicketTypeText(elementIndex)
    );
    return this.travelerShinkansenTicketTypesCache[cacheKey];
  }

  get isJrPackage() {
    return this.orderItemCategory === 'jr_package';
  }

  get isPackage() {
    return [4, 9, 10, 11, 12].includes(this.orderItemType || 0);
  }

  handleRemoveElement(element: Element) {
    // 要素を削除
    const removeFromArray = <T>(arr: T[], item: T) => {
      const index = arr.indexOf(item);
      if (index > -1) {
        arr.splice(index, 1);
      }
    };

    removeFromArray(this.transports, element);
    removeFromArray(this.hotels, element);
    removeFromArray(this.cancelCharges, element);
    removeFromArray(this.changeFees, element);
    removeFromArray(this.supportFees, element);
    removeFromArray(this.shippings, element);
    removeFromArray(this.wifis, element);
    removeFromArray(this.rooms, element);
    removeFromArray(this.kits, element);
    removeFromArray(this.travelAuthorizations, element);
    removeFromArray(this.insurances, element);
    removeFromArray(this.elements, element);
  }

  handleChangeType(index: number, value: string): void {
    const currentElement = this.elements[index];
    let newElement: Element | undefined;

    const startTimestamp = this.getStartTimestamp();
    const endTimestamp = this.getEndTimestamp();
    const startDate = startTimestamp ? startTimestamp.clone() : moment();
    const endDate = endTimestamp ? endTimestamp.clone() : startDate.clone().add(1, 'day');

    switch (value) {
      case 'transport':
        newElement = new TransportElement({
          id: 0,
          transportType: 'domestic_air',
          cabin: 'economy', // API側で定義されている
          airTicketType: 0,
          from: new TransportElementTerminal({
            time: moment(),
            timeZone: 'Asia/Tokyo'
          }),
          to: new TransportElementTerminal({
            time: moment(),
            timeZone: 'Asia/Tokyo'
          })
        });
        break;
      case 'hotel':
        newElement = new HotelElement({
          id: 0,
          name: '',
          roomNum: 1,
          checkinDate: startDate,
          checkoutDate: endDate,
          provider: '',
          providerOptions: this.hotelElementProviderOptions,
          planName: '',
          roomType: '',
          restricted: false,
          tooLate: false,
          hotelElementGuests: []
        });
        break;
      case 'cancel':
        newElement = new CancelChargeElement({
          id: 0,
          content: '',
          canceledAt: moment(),
          startAt: startDate,
          endAt: endDate
        });
        break;
      case 'change_fee':
        newElement = new ChangeFeeElement({
          id: 0,
          content: '',
          canceledAt: moment(),
          startAt: startDate,
          endAt: endDate
        });
        break;
      case 'support_fee':
        newElement = new SupportFeeElement({
          id: 0,
          content: '',
          reservedAt: moment()
        });
        break;
      case 'shipping':
        newElement = new ShippingElement({
          id: 0,
          shippingCategoryOptions: this.shippingCategoryOptions
        });
        break;
      case 'wifi':
        newElement = new WifiElement({
          id: 0,
          summary: '',
          providerOptions: this.wifiProviderOptions
        });
        break;
      case 'room':
        newElement = new RoomElement({
          id: 0,
          name: '',
          address: '',
          startedAt: startDate,
          endedAt: endDate
        });
        break;
      case 'kit':
        newElement = new KitElement({
          id: 0,
          name: '',
          stockNumber: ''
        });
        break;
      case 'travel_authorization':
        newElement = new TravelAuthorizationElement({
          id: 0,
          serviceText: '',
          content: ''
        });
        break;
      case 'insurance':
        newElement = new InsuranceElement({
          id: 0,
          productText: ''
        });
        break;
      default:
        // No action for unknown types
        break;
    }

    if (newElement) {
      // 現在の要素を削除
      this.handleRemoveElement(currentElement);

      // 新しい要素を適切な配列に追加
      if (newElement instanceof TransportElement) {
        this.transports.push(newElement);
      } else if (newElement instanceof HotelElement) {
        this.hotels.push(newElement);
      } else if (newElement instanceof CancelChargeElement) {
        this.cancelCharges.push(newElement);
      } else if (newElement instanceof ChangeFeeElement) {
        this.changeFees.push(newElement);
      } else if (newElement instanceof SupportFeeElement) {
        this.supportFees.push(newElement);
      } else if (newElement instanceof ShippingElement) {
        this.shippings.push(newElement);
      } else if (newElement instanceof WifiElement) {
        this.wifis.push(newElement);
      } else if (newElement instanceof RoomElement) {
        this.rooms.push(newElement);
      } else if (newElement instanceof KitElement) {
        this.kits.push(newElement);
      } else if (newElement instanceof TravelAuthorizationElement) {
        this.travelAuthorizations.push(newElement);
      } else if (newElement instanceof InsuranceElement) {
        this.insurances.push(newElement);
      }

      // elements配列を更新
      this.elements[index] = newElement;
    }
  }

  get isDomesticAir(): boolean {
    return this.transports.some(t => t.isDomesticAir);
  }

  get isForeignAir(): boolean {
    return this.transports.some(t => t.transportType === 'foreign_air');
  }

  handleTransportTypeChange(transportElement: TransportElement, value: TransportType): void {
    transportElement.transportType = value;
    this.updatePriceMarginType();
    this.fetchCategory();
  }

  updatePriceMarginType(): void {
    if (this.price && this.marginType) {
      this.price.marginType = this.marginType;
    }
  }

  async fetchCategory(): Promise<void> {
    try {
      const result = await Fetcher.get<{ order_item_category: string }>(
        '/arrangement/order_item_categories',
        this.updateParams as Record<string, unknown>,
        { parser: 'jquery' }
      );
      this.orderItemCategory = result.order_item_category;
    } catch (error) {
      console.error('Failed to fetch category:', error);
    }
  }

  get description(): string {
    const descriptions: string[] = [];

    descriptions.push('---');
    if (this.orderItemCategory === 'arrangement_request') {
      descriptions.push('リクエストフォーム利用料');
    }

    this.elements.forEach(element => {
      if (element.description) {
        descriptions.push(element.description);
      }
    });

    descriptions.push(`\n${this.price.description}`);

    return descriptions.join('\n');
  }

  get totalPrice(): number {
    return this.price?.totalPrice || 0;
  }

  isNeedReCalcMarginAmount(): boolean {
    if (!this.price) {
      return false;
    }
    return this.price.isNeedReCalcMarginAmount();
  }

  validationErrors(): string[][] {
    const errors: string[][] = [];

    // Check validation errors from elements
    this.elements.forEach(element => {
      const elementErrors = this.getElementValidationErrors(element);
      if (elementErrors && elementErrors.length > 0) {
        errors.push(elementErrors);
      }
    });

    // Check price validation errors
    if (this.price) {
      const priceErrors = this.price.validationErrors;
      if (priceErrors && priceErrors.length > 0) {
        errors.push(priceErrors);
      }
    }

    // Check if there are travelers
    if (this.travelerInformations.length === 0) {
      errors.push(['出張者が誰もいません']);
    }

    // Check payment possibility
    if (!this.isPaymentPossible()) {
      errors.push(['海外航空券はカード払いでは購入できません']);
    }

    // Check foreign flight ticket issuer
    if (this.foreignFlightTicketIssuer === 'tsd' && (!this.tsdInvoiceNumber || this.tsdInvoiceNumber === '')) {
      errors.push(['発券先がティ・エス・ディの場合は、ティ・エス・ディ請求書番号を入力してください']);
    }

    return errors;
  }

  private getElementValidationErrors(element: Element): string[] {
    // Elements should have their own validation logic
    // This is a placeholder - actual implementation depends on element types
    if ('validationErrors' in element && typeof element.validationErrors === 'function') {
      return element.validationErrors(this.status);
    }
    return [];
  }

  isPaymentPossible(): boolean {
    // Check if foreign air tickets can be paid with credit card
    const foreignFlights = this.transports.filter(t => t.isForeignAir);

    // Assuming payment type 0 is credit card
    if (this.payment && this.payment.paymentType === 0 && foreignFlights.length > 0) {
      // Credit card payment + foreign air tickets -> not possible
      return false;
    }
    return true;
  }

  categoryStr(): string {
    switch (this.orderItemCategory) {
      case 'hotel':
      case 'domestic_hotel':
      case 'foreign_hotel':
        return 'ホテル';
      case 'domestic_air':
      case 'foreign_air':
        return '航空券';
      case 'shinkansen':
        return '新幹線';
      case 'express':
        return '特急';
      case 'railway_ticket':
        return '在来線';
      case 'rental_car':
        return 'レンタカー';
      case 'wbf_package':
      case 'jr_package':
        return 'パッケージ';
      case 'cancel':
        return 'キャンセル料';
      case 'other':
        return 'その他';
      case 'support_fee':
        return '出張サポート費';
      case 'change_fee':
        return '変更手数料';
      case 'shipping':
        return '配送料';
      case 'wifi':
        return 'WiFi';
      case 'room':
        return '会議室';
      case 'kit':
        return '検査キット';
      case 'bus':
        return 'バス';
      case 'ana_tabisaku':
        return 'ANA旅作';
      case 'arrangement_request':
        return 'リクエストフォーム利用料';
      case 'travel_authorization':
        return '査証・渡航認証関連';
      case 'insurance':
        return '保険';
      case 'taxi':
        return 'タクシー';
      default:
        return '';
    }
  }

  get mappingDescription(): string {
    const firstElement = this.elements[0];
    if (firstElement && 'mappingDescription' in firstElement) {
      const value = (firstElement as { mappingDescription?: unknown }).mappingDescription;
      if (typeof value === 'function') {
        const desc = value.call(firstElement);
        if (typeof desc === 'string' && desc.length > 0) return desc;
      } else if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }
    return this.categoryStr();
  }

  get updateParams() {
    if (this.elements.length === 0 && this.orderItemCategory !== 'arrangement_request') {
      return null;
    }

    const params: Record<string, unknown> = {
      price: this.price.updateParams,
      payment: this.payment.updateParams,
      elements: this.elements.map(e => {
        if ('updateParams' in e && e.updateParams) {
          return e.updateParams;
        }
        return {};
      }),
      people_num: this.peopleNum,
      trace_id: this.traceId,
      modified: this.isEditing,
      job_type: this.jobType,
      status: this.status,
      billed_at: this.billedAt?.format(),
      order_item_category: this.orderItemCategory,
      pnr_id: this.pnrId,
      foreign_flight_ticket_issuer: this.foreignFlightTicketIssuer,
      tsd_invoice_number: this.tsdInvoiceNumber,
      // 以下3つのパラメーターは外からupdateできないため送らない
      // ticketed_at: this.ticketedAt?.format('YYYY-MM-DD HH:mm:ss'),
      // delivered_at: this.deliveredAt?.format('YYYY-MM-DD HH:mm:ss'),
      // approved_at: this.approvedAt?.format('YYYY-MM-DD HH:mm:ss'),
      jr_auth_code: this.jrAuthCode,
      jr_pickup_code: this.jrPickupCode,
      purchase_deadline: this.purchaseDeadline?.format(),
      traveler_informations: this.travelerInformations.map(t => t.updateParams)
    };

    return params;
  }

  get isTicketingExpired() {
    return this.transports.some(t => t.isForeignAir && t.isTicketingExpired);
  }

  diffWith(orderItem: OrderItem | null | undefined): Record<string, unknown> | null {
    if (!orderItem) {
      return null;
    }

    // mobxのプロキシオブジェクトをcloneしようとするとエラーが出るため、toJSでプレーンなオブジェクトに変換してからcloneする
    const thisItem = cloneDeep(toJS(this));
    const originalItem = cloneDeep(toJS(orderItem));

    delete thisItem.initialData;
    delete originalItem.initialData;

    // order未実装。orderパラメータが必要になって実装したらコメント外す
    // delete thisItem.order;
    // delete originalItem.order;

    return diff(originalItem, thisItem) as Record<string, unknown>;
  }

  handleAddElementByType(e: React.MouseEvent): void {
    e.preventDefault();
    let element: Element | undefined;

    switch (this.additionalType) {
      case 'hotel':
        element = new HotelElement({
          id: 0,
          name: '',
          checkinDate: moment(),
          checkoutDate: moment().add(1, 'day'),
          address: '',
          roomType: '',
          provider: '',
          providerOptions: this.hotelElementProviderOptions,
          hotelReservationNumber: '',
          roomNum: 1,
          planName: '',
          restricted: false,
          tooLate: false,
          hotelElementGuests: []
        });
        break;
      case 'transport':
        element = new TransportElement({
          id: 0,
          transportType: 'domestic_air',
          from: new TransportElementTerminal({
            name: '',
            time: moment(),
            timeZone: 'Asia/Tokyo'
          }),
          to: new TransportElementTerminal({
            name: '',
            time: moment(),
            timeZone: 'Asia/Tokyo'
          }),
          name: '',
          cabin: '',
          airTicketType: 0
        });
        break;
      case 'cancel':
        element = new CancelChargeElement({
          id: 0,
          content: ''
        });
        break;
      case 'change_fee':
        element = new ChangeFeeElement({
          id: 0,
          content: ''
        });
        break;
      case 'support_fee':
        element = new SupportFeeElement({
          id: 0,
          content: '',
          reservedAt: moment()
        });
        break;
      case 'shipping':
        element = new ShippingElement({
          id: 0,
          shippingCategoryOptions: this.shippingCategoryOptions
        });
        break;
      case 'wifi':
        element = new WifiElement({
          id: 0,
          summary: '',
          providerOptions: this.wifiProviderOptions
        });
        break;
      case 'room':
        element = new RoomElement({
          id: 0,
          name: '',
          address: '',
          startedAt: moment(),
          endedAt: moment()
        });
        break;
      case 'kit':
        element = new KitElement({
          id: 0,
          name: '',
          stockNumber: ''
        });
        break;
      case 'travel_authorization':
        element = new TravelAuthorizationElement({
          id: 0,
          serviceText: '',
          content: ''
        });
        break;
      case 'insurance':
        element = new InsuranceElement({
          id: 0,
          productText: ''
        });
        break;
      default:
        break;
    }

    if (element) {
      this.elements.push(element);
      this.updatePriceShowFee();
      this.updatePriceMarginType();
      this.updateTravelerInformationShinkansenTypes('add');
      this.handlePeopleNumChange(this.travelerInformations.length);
      this.fetchCategory();
    }
  }

  updatePriceShowFee(): void {
    if (this.showFee || !this.price) {
      return;
    }
    this.price.showFee = false;
  }

  updateTravelerInformationShinkansenTypes(action: 'add' | 'remove' | 'init', index = 0): void {
    this.travelerInformations.forEach(traveler => {
      if (
        !traveler.travelerInformationShinkansenTypes ||
        traveler.travelerInformationShinkansenTypes.length === 0
      ) {
        return;
      }

      if (action === 'add') {
        const lastElement = this.elements[this.elements.length - 1];
        let shinkansenTicketType = 'normal';
        let shinkansenSeatType = 'normal';

        if (traveler.shinkansenTicketType) {
          shinkansenTicketType = traveler.shinkansenTicketType;
        } else if (
          lastElement &&
          lastElement instanceof TransportElement &&
          lastElement.railwayTicketType !== undefined
        ) {
          switch (lastElement.railwayTicketType) {
            case 0:
              shinkansenTicketType = 'normal';
              break;
            case 1:
              shinkansenTicketType = 'kyuusyuu';
              break;
            case 2:
              shinkansenTicketType = 'bulk_ticket';
              break;
            case 3:
              shinkansenTicketType = 'ex';
              break;
            default:
              shinkansenTicketType = 'normal';
          }
        }

        if (traveler.shinkansenSeatType) {
          shinkansenSeatType = traveler.shinkansenSeatType;
        } else if (
          lastElement &&
          lastElement instanceof TransportElement &&
          lastElement.railwaySeatType !== undefined
        ) {
          switch (lastElement.railwaySeatType) {
            case 0:
              shinkansenSeatType = 'reserve';
              break;
            case 1:
              shinkansenSeatType = 'free';
              break;
            case 2:
              shinkansenSeatType = 'green';
              break;
            default:
              shinkansenSeatType = 'normal';
          }
        }

        const shinkansenType = new TravelerShinkansenType({
          travelerInformationId: traveler.id,
          shinkansenTicketType,
          shinkansenSeatType
        });
        traveler.travelerInformationShinkansenTypes.push(shinkansenType);
      } else if (action === 'remove') {
        traveler.travelerInformationShinkansenTypes.splice(index, 1);
      }
    });
  }

  handlePeopleNumChange(value: number): void {
    this.peopleNum = value;
    this.elements.forEach(el => {
      if ('peopleNum' in el) {
        (el as HotelElement | TransportElement).peopleNum = value;
      }
    });

    if (this.price) {
      this.price.peopleNum = value;
    }
  }

  updateTraveler(traveler: TravelerInformation): void {
    const index = this.travelerInformations.findIndex(t => t.id === traveler.id);
    if (index !== -1) {
      this.travelerInformations.splice(index, 1, traveler);
      this.handlePeopleNumChange(this.travelerInformations.length);
    }
  }

  removeTraveler(travelerId: number): void {
    const index = this.travelerInformations.findIndex(t => t.id === travelerId);
    if (index !== -1) {
      this.travelerInformations.splice(index, 1);
      this.handlePeopleNumChange(this.travelerInformations.length);
    }
  }

  isShinkansenOnly(): boolean {
    return this.elements.every(element => {
      return (
        element.type === 'transport' &&
        element instanceof TransportElement &&
        (element.isShinkansen || element.isExpress)
      );
    });
  }

  get isAvailableBulkTicket(): boolean {
    return this.isShinkansenOnly() && (this.payment?.isBulkTicket() ?? false);
  }

  get isChangeableExist(): boolean {
    return this.elements.some(element => element instanceof TransportElement && element.isChangeable);
  }

  get travelerAirlineTypes() {
    if (this.orderItemCategory !== 'domestic_air' && this.orderItemCategory !== 'foreign_air') return [];

    if (this.orderItemCategory === 'domestic_air') {
      return this.travelerInformations.map(travelerInformation => travelerInformation.travelerAirlineTypeDomestic);
    }

    return this.travelerInformations.map(travelerInformation => travelerInformation.travelerAirlineTypeForeign);
  }

  get isCanceled() {
    return (this.jobType === 'cancel' && this.status && [7, 13].includes(this.status)) || false;
  }

  get hasNotEnded() {
    const endDates = this.elements
      .filter(element => element.endTime)
      .map(element => element.endTime)
      .filter(t => !!t);

    if (endDates.length === 0) return false;

    const lastEndDate = endDates.sort((a, b) => a.toDate().getTime() - b.toDate().getTime()).pop();

    return lastEndDate ? lastEndDate.isAfter(moment()) : false;
  }

  get startTime() {
    const times = this.elements.map(element => element.startTime).filter(t => !!t);
    if (times.length === 0) return null;
    return times.sort((a, b) => a.utcOffset() - b.utcOffset()).pop();
  }

  get endTime() {
    const times = this.elements.map(element => element.endTime).filter(t => !!t);
    if (times.length === 0) return null;
    return times.sort((a, b) => a.utcOffset() - b.utcOffset()).pop();
  }

  // OrderからOrderItemを経由しないでElementのstartTimeを参照しないように責務を分けている
  getStartTimestamp(): Moment | null {
    const times = this.elements
      .map(element => {
        const typedElement = element as { startTime?: Moment | null };
        return typedElement.startTime ?? null;
      })
      .filter((time): time is Moment => !!time);

    if (times.length === 0) {
      return null;
    }

    // OrderItemが複数のElementを持つ場合、最小のstartTimeを返す
    return times.sort((a, b) => a.valueOf() - b.valueOf())[0];
  }

  // OrderからOrderItemを経由しないでElementのendTimeを参照しないように責務を分けている
  getEndTimestamp(): Moment | null {
    const times = this.elements
      .map(element => {
        const typedElement = element as { endTime?: Moment | null };
        return typedElement.endTime ?? null;
      })
      .filter((time): time is Moment => !!time);

    if (times.length === 0) {
      return null;
    }

    // OrderItemが複数のElementを持つ場合、最大のendTimeを返す
    return times.sort((a, b) => b.valueOf() - a.valueOf())[0];
  }

  isFollowingElement(element: Element): boolean {
    if (!(element instanceof TransportElement) || !element.isForeignAir) {
      return false;
    }
    const firstForeignAir = this.transports.find(t => t.isForeignAir);
    return !!firstForeignAir && firstForeignAir.id !== element.id;
  }

  isPrecedingElement(element: Element): boolean {
    if (!(element instanceof TransportElement) || !element.isForeignAir) {
      return false;
    }
    const lastForeignAir = this.transports.filter(t => t.isForeignAir).pop();
    return !!lastForeignAir && lastForeignAir.id !== element.id;
  }

  travelerShinkansenTypes(elementIndex: number | null = null) {
    if (this.orderItemCategory !== 'shinkansen' && this.orderItemCategory !== 'express') return [];

    return this.travelerInformations.map(travelerInformation =>
      travelerInformation.travelerShinkansenType(elementIndex)
    );
  }
}
